import { create } from 'zustand';
import {
  fetchConditions,
  createCondition,
  updateCondition,
  deleteCondition,
  parseCondition,
  resetDefaultConditions,
  evaluateConditions,
} from '../api/conditions';
import type { ConditionRule, ParsedCondition, Candidate, ConditionCategory } from '../types';

/**
 * 条件管理 Store
 * 管理：条件列表 + AI 解析状态
 */
interface ConditionState {
  /** 条件列表 */
  conditions: ConditionRule[];
  /** 当前 Tab 分类 */
  activeCategory: ConditionCategory;
  /** AI 解析结果 */
  parsedResult: ParsedCondition | null;
  /** 筛选结果 */
  candidates: Candidate[];
  /** 加载状态 */
  loading: boolean;
  /** AI 解析中 */
  parsing: boolean;
  /** 筛选中 */
  evaluating: boolean;
  /** 错误信息 */
  error: string | null;

  /** 获取条件列表 */
  fetchConditions: () => Promise<void>;
  /** 设置当前 Tab */
  setActiveCategory: (category: ConditionCategory) => void;
  /** 新增条件 */
  createCondition: (condition: Partial<ConditionRule>) => Promise<void>;
  /** 编辑条件 */
  updateCondition: (id: number, condition: Partial<ConditionRule>) => Promise<void>;
  /** 删除条件 */
  deleteCondition: (id: number) => Promise<void>;
  /** AI 解析 */
  parseCondition: (text: string) => Promise<void>;
  /** 清除解析结果 */
  clearParsed: () => void;
  /** 恢复默认预设 */
  resetDefaults: () => Promise<void>;
  /** 手动触发筛选 */
  evaluate: () => Promise<void>;
}

export const useConditionStore = create<ConditionState>((set, get) => ({
  conditions: [],
  activeCategory: 'buy',
  parsedResult: null,
  candidates: [],
  loading: false,
  parsing: false,
  evaluating: false,
  error: null,

  fetchConditions: async () => {
    set({ loading: true, error: null });
    try {
      const conditions = await fetchConditions();
      set({ conditions, loading: false });
    } catch (error: any) {
      set({ loading: false, error: error.message });
    }
  },

  setActiveCategory: (category: ConditionCategory) => {
    set({ activeCategory: category });
  },

  createCondition: async (condition: Partial<ConditionRule>) => {
    set({ error: null });
    try {
      await createCondition(condition);
      await get().fetchConditions();
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  updateCondition: async (id: number, condition: Partial<ConditionRule>) => {
    set({ error: null });
    try {
      await updateCondition(id, condition);
      await get().fetchConditions();
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  deleteCondition: async (id: number) => {
    set({ error: null });
    try {
      await deleteCondition(id);
      await get().fetchConditions();
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  parseCondition: async (text: string) => {
    set({ parsing: true, error: null, parsedResult: null });
    try {
      const result = await parseCondition(text);
      set({ parsedResult: result, parsing: false });
    } catch (error: any) {
      set({ parsing: false, error: error.message });
      throw error;
    }
  },

  clearParsed: () => {
    set({ parsedResult: null });
  },

  resetDefaults: async () => {
    set({ loading: true, error: null });
    try {
      const conditions = await resetDefaultConditions();
      set({ conditions, loading: false });
    } catch (error: any) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  evaluate: async () => {
    set({ evaluating: true, error: null });
    try {
      const candidates = await evaluateConditions();
      set({ candidates, evaluating: false });
    } catch (error: any) {
      set({ evaluating: false, error: error.message });
      throw error;
    }
  },
}));
